import {HStack} from "@chakra-ui3/react";
import {Owner, OwnerType, RegisteredUser} from "../../Types.ts";
import {useCallback, useState} from "react";
import {UserPicker, UserPickerValue} from "../../components/UserPicker.tsx";
import api from "../../api/index.ts";
import {usePromiseState} from "../../hooks/promiseState.ts";
import {InnerDialogProps, withDialog} from "../../state/withDialog.tsx";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../components/ui/dialog.tsx";
import {Button} from "../../components/ui/button.tsx";
import {useModalContext} from "../../components/modals";
import {inviteUserDialog} from ".";

const ReassignUserDialog = withDialog(
  ({
    onClose,
    onReassign,
  }: InnerDialogProps & {
    onReassign: (owner?: Owner) => Promise<unknown>;
  }) => {
    const {open} = useModalContext();
    const [userOrInvite, setUserOrInvite] = useState<UserPickerValue>({type: "Invite", nameOrEmail: ""});

    const initialInviteName =
      userOrInvite?.type === "Invite" && !userOrInvite.nameOrEmail.includes("@") ? userOrInvite.nameOrEmail : "";
    const initialInviteEmail =
      userOrInvite?.type === "Invite" && userOrInvite.nameOrEmail.includes("@") ? userOrInvite.nameOrEmail : "";

    const reassignUser = useCallback(
      async (user: RegisteredUser) => {
        const owner = await api.owners.resolve([{type: OwnerType.User, content: user.user_id}]);
        await onReassign(owner[0]);
      },
      [onReassign],
    );

    const [reassigning, reassign] = usePromiseState(async () => {
      if (userOrInvite?.type === "User") {
        const owner = await api.owners.resolve([{type: OwnerType.User, content: userOrInvite.user.user_id}]);
        onReassign(owner[0]);
      } else if (userOrInvite?.type === "Invite") {
        // Give this modal time to close or else auto-focus doesn't work properly
        setTimeout(() => {
          open(inviteUserDialog, {
            initialName: initialInviteName,
            initialEmail: initialInviteEmail,
            onInvite: reassignUser,
          });
        }, 200);
      }
      onClose();
    }, [onReassign, onClose, open, userOrInvite, initialInviteName, initialInviteEmail, reassignUser]);

    return (
      <>
        <DialogHeader>
          <DialogTitle>Reassign to</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <UserPicker value={userOrInvite} onSelect={setUserOrInvite} onSubmit={reassign} autoFocus />
        </DialogBody>
        <DialogFooter>
          <HStack gap="3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorPalette="blue"
              onClick={reassign}
              disabled={!userOrInvite || reassigning.inProgress}
              loading={reassigning.inProgress}
            >
              {userOrInvite?.type === "Invite" ? "Invite and reassign" : "Reassign"}
            </Button>
          </HStack>
        </DialogFooter>
      </>
    );
  },
);

export default ReassignUserDialog;
